import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  type UploadTaskSnapshot,
} from 'firebase/storage';
import { storage } from './firebase';

export interface UploadedFile {
  id: string;
  name: string;
  originalName: string;
  url: string;
  type: string;
  size: number;
  uploadedAt: number;
}

export interface UploadProgress {
  progress: number;
  bytesTransferred: number;
  totalBytes: number;
}

// Allowed file types for announcements
export const ALLOWED_FILE_TYPES = {
  images: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
  documents: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  spreadsheets: ['application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
  presentations: ['application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'],
};

export const ALL_ALLOWED_TYPES = [
  ...ALLOWED_FILE_TYPES.images,
  ...ALLOWED_FILE_TYPES.documents,
  ...ALLOWED_FILE_TYPES.spreadsheets,
  ...ALLOWED_FILE_TYPES.presentations,
];

// Max file size: 10MB
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

// Max attachments per announcement
export const MAX_ATTACHMENTS = 5;

// ===========================
// 1. UPLOAD FILE
// ===========================

export async function uploadFile(
  domain: string,
  file: File,
  folder: string = 'announcements',
  onProgress?: (progress: UploadProgress) => void
): Promise<{ success: boolean; file?: UploadedFile; error?: string }> {
  try {
    // Validate file type
    if (!ALL_ALLOWED_TYPES.includes(file.type)) {
      return {
        success: false,
        error: `File type not allowed. Allowed types: Images, PDF, Word, Excel, PowerPoint`,
      };
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      return {
        success: false,
        error: `File too large. Maximum size is ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
      };
    }

    // Generate unique filename
    const timestamp = Date.now();
    const uniqueId = Math.random().toString(36).substring(2, 9);
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const fileName = `${timestamp}_${uniqueId}_${safeName}`;

    // Create storage reference
    const storageRef = ref(storage, `${domain}/${folder}/${fileName}`);

    // Upload file with progress tracking
    const uploadTask = uploadBytesResumable(storageRef, file);

    return new Promise((resolve) => {
      uploadTask.on(
        'state_changed',
        (snapshot: UploadTaskSnapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          if (onProgress) {
            onProgress({
              progress,
              bytesTransferred: snapshot.bytesTransferred,
              totalBytes: snapshot.totalBytes,
            });
          }
        },
        (error) => {
          console.error('Upload error:', error);
          resolve({ success: false, error: error.message });
        },
        async () => {
          // Upload complete, get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);

          const uploadedFile: UploadedFile = {
            id: uniqueId,
            name: fileName,
            originalName: file.name,
            url: downloadURL,
            type: file.type,
            size: file.size,
            uploadedAt: timestamp,
          };

          console.log('✅ File uploaded successfully:', uploadedFile.originalName);
          resolve({ success: true, file: uploadedFile });
        }
      );
    });
  } catch (error: any) {
    console.error('Error uploading file:', error);
    return { success: false, error: error.message };
  }
}

// ===========================
// 2. UPLOAD MULTIPLE FILES
// ===========================

export async function uploadMultipleFiles(
  domain: string,
  files: File[],
  folder: string = 'announcements',
  onProgress?: (fileIndex: number, progress: UploadProgress) => void
): Promise<{ success: boolean; files: UploadedFile[]; errors: string[] }> {
  const uploadedFiles: UploadedFile[] = [];
  const errors: string[] = [];

  // Limit number of files
  const filesToUpload = files.slice(0, MAX_ATTACHMENTS);

  for (let i = 0; i < filesToUpload.length; i++) {
    const file = filesToUpload[i];
    const result = await uploadFile(
      domain,
      file,
      folder,
      onProgress ? (progress) => onProgress(i, progress) : undefined
    );

    if (result.success && result.file) {
      uploadedFiles.push(result.file);
    } else {
      errors.push(`${file.name}: ${result.error}`);
    }
  }

  return {
    success: errors.length === 0,
    files: uploadedFiles,
    errors,
  };
}

// ===========================
// 3. DELETE FILE
// ===========================

export async function deleteFile(
  domain: string,
  fileName: string,
  folder: string = 'announcements'
): Promise<{ success: boolean; error?: string }> {
  try {
    const storageRef = ref(storage, `${domain}/${folder}/${fileName}`);
    await deleteObject(storageRef);
    console.log('✅ File deleted:', fileName);
    return { success: true };
  } catch (error: any) {
    console.error('Error deleting file:', error);
    return { success: false, error: error.message };
  }
}

// ===========================
// 4. GET FILE ICON
// ===========================

export function getFileIcon(fileType: string): string {
  if (ALLOWED_FILE_TYPES.images.includes(fileType)) {
    return 'bi-file-image';
  }
  if (fileType === 'application/pdf') {
    return 'bi-file-pdf';
  }
  if (ALLOWED_FILE_TYPES.documents.includes(fileType)) {
    return 'bi-file-word';
  }
  if (ALLOWED_FILE_TYPES.spreadsheets.includes(fileType)) {
    return 'bi-file-excel';
  }
  if (ALLOWED_FILE_TYPES.presentations.includes(fileType)) {
    return 'bi-file-ppt';
  }
  return 'bi-file-earmark';
}

// ===========================
// 5. GET FILE COLOR
// ===========================

export function getFileColor(fileType: string): string {
  if (ALLOWED_FILE_TYPES.images.includes(fileType)) {
    return '#28a745'; // Green
  }
  if (fileType === 'application/pdf') {
    return '#dc3545'; // Red
  }
  if (ALLOWED_FILE_TYPES.documents.includes(fileType)) {
    return '#2D7DD2'; // Blue
  }
  if (ALLOWED_FILE_TYPES.spreadsheets.includes(fileType)) {
    return '#28a745'; // Green
  }
  if (ALLOWED_FILE_TYPES.presentations.includes(fileType)) {
    return '#fd7e14'; // Orange
  }
  return '#6c757d'; // Gray
}

// ===========================
// 6. FORMAT FILE SIZE
// ===========================

export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

// ===========================
// 7. VALIDATE FILES
// ===========================

export function validateFiles(files: FileList | File[]): {
  valid: File[];
  errors: string[];
} {
  const valid: File[] = [];
  const errors: string[] = [];

  const fileArray = Array.from(files);

  if (fileArray.length > MAX_ATTACHMENTS) {
    errors.push(`Maximum ${MAX_ATTACHMENTS} attachments allowed`);
  }

  fileArray.slice(0, MAX_ATTACHMENTS).forEach((file) => {
    if (!ALL_ALLOWED_TYPES.includes(file.type)) {
      errors.push(`${file.name}: File type not allowed`);
    } else if (file.size > MAX_FILE_SIZE) {
      errors.push(`${file.name}: File too large (max ${MAX_FILE_SIZE / (1024 * 1024)}MB)`);
    } else {
      valid.push(file);
    }
  });

  return { valid, errors };
}
